import {
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import commaNumber from 'comma-number';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Scatter } from 'react-chartjs-2';

import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import useModalState from '@/hooks/pure/useModalState';
import { Tooltip } from '@/shared/ui/tooltip/tooltip';
import { getChartColorByHash } from '@/styles/colors/chartColors';
import colors from '@/styles/colors/color';
import { ConstMap } from '@/utils/ConstMap';

import { getPeriodLabel } from '../../lib/signal-date-preset.utils';
import { getChangePercentage } from '../../lib/signal-issue.utils';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '../../model';
import { IssueActionDropdown } from '../chart.issue-action-dropdown';
import { IssueLegend } from '../chart.issue-legend';

ChartJS.register(LinearScale, PointElement, LineElement, ChartTooltip, Legend);

const MATRIX_INFO_CONFIG = new ConstMap<
  'Dominant' | 'Established' | 'Rising' | 'Niche',
  { label: string; color: string; legendColor: string }
>([
  [
    'Dominant',
    {
      label: 'High Volume, High Change',
      color: colors.red50,
      legendColor: colors.red200,
    },
  ],
  [
    'Established',
    {
      label: 'High Volume, Low Change',
      color: colors.yellow50,
      legendColor: colors.yellow200,
    },
  ],
  [
    'Rising',
    {
      label: 'Low Volume, High Change',
      color: colors.green50,
      legendColor: colors.green200,
    },
  ],
  [
    'Niche',
    {
      label: 'Low Volume, Low Change',
      color: colors.dune50,
      legendColor: colors.dune200,
    },
  ],
]);

const quadrantBackgroundPlugin = {
  id: 'quadrantBackground',
  beforeDraw(
    chart: ChartJS,
    args: any,
    options: {
      medianX: number;
      medianY: number;
      colors: {
        topRight: string;
        topLeft: string;
        bottomRight: string;
        bottomLeft: string;
        border: string;
      };
    },
  ) {
    const {
      ctx,
      chartArea: { top, bottom, left, right },
      scales: { x, y },
    } = chart;
    const { medianX, medianY, colors } = options;

    const xMedianPixel = x.getPixelForValue(medianX);
    const yMedianPixel = y.getPixelForValue(medianY);

    ctx.save();

    // 1. 먼저 모든 분면 배경 그리기
    // Top-left 배경
    ctx.fillStyle = colors.topLeft;
    ctx.fillRect(left, top, xMedianPixel - left, yMedianPixel - top);

    // Top-right 배경
    ctx.fillStyle = colors.topRight;
    ctx.fillRect(xMedianPixel, top, right - xMedianPixel, yMedianPixel - top);

    // Bottom-left 배경
    ctx.fillStyle = colors.bottomLeft;
    ctx.fillRect(
      left,
      yMedianPixel,
      xMedianPixel - left,
      bottom - yMedianPixel,
    );

    // Bottom-right 배경
    ctx.fillStyle = colors.bottomRight;
    ctx.fillRect(
      xMedianPixel,
      yMedianPixel,
      right - xMedianPixel,
      bottom - yMedianPixel,
    );

    ctx.fillStyle = colors.border;
    ctx.fillRect(xMedianPixel - 0.5, top, 1, bottom - top); // 세로 경계선
    ctx.fillRect(left, yMedianPixel - 0.5, right - left, 1); // 가로 경계선

    ctx.restore();
  },
};

/**
 * 배열에서 p번째 백분위수 값을 반환 (보간 없이 nearest-rank 방식).
 * 예: percentile([10,20,30,40,50], 0.5) → 30 (중앙값)
 */
const percentile = (arr: number[], p: number): number => {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.ceil(sorted.length * p) - 1;
  return sorted[Math.max(0, Math.min(sorted.length - 1, idx))];
};

export const MatrixView = (props: {
  data: CompositedSignalIssue[];
  dynamicFilterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
  topCount: number;
}) => {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const chartRef = useRef(null);
  const { data, dynamicFilterSet, datePreset } = props;
  const dropdownState = useModalState<CompositedSignalIssue | null>(null);
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<Set<string>>(new Set());
  const [_isolatedSeriesId, setIsolatedSeriesId] = useState<string | null>(null);
  const [preIsolateHiddenIds, setPreIsolateHiddenIds] = useState<Set<string> | null>(null);
  const slicedData = props.topCount > 0 ? data.slice(0, props.topCount) : data;

  const axisConfig = useMemo(() => {
    // 유한한 x값과 y값 수집
    const finiteXValues: number[] = [];
    const yValues: number[] = [];

    slicedData.forEach((issue) => {
      const change = getChangePercentage(
        issue.comparisonPeriodCount,
        issue.selectedPeriodCount,
      );
      if (change !== null && Number.isFinite(change)) {
        finiteXValues.push(change);
      }
      yValues.push(issue.selectedPeriodCount);
    });

    // X축 범위 계산 (유한 값만 대상)
    const xDataMin =
      finiteXValues.length > 0
        ? finiteXValues.reduce((a, b) => Math.min(a, b))
        : -100;
    const xDataMax =
      finiteXValues.length > 0
        ? finiteXValues.reduce((a, b) => Math.max(a, b))
        : 100;
    const xRange = xDataMax - xDataMin || Math.max(1, Math.abs(xDataMax) * 0.1);
    const xPadding = xRange * 0.1;

    // "New" 아이템 위치: 유한 데이터 범위 오른쪽 끝 바깥
    const newItemX = xDataMax + xPadding * 2;

    // "New" 아이템 인식 임계값 (틱 라벨용)
    const newItemThreshold = newItemX - xRange * 0.01;

    // 최종 축 경계 (New 아이템 영역 포함)
    const xMin = xDataMin - xPadding;
    const xMax = newItemX + xPadding;

    // Y축 범위 계산
    const yDataMin =
      yValues.length > 0 ? yValues.reduce((a, b) => Math.min(a, b)) : 0;
    const yDataMax =
      yValues.length > 0 ? yValues.reduce((a, b) => Math.max(a, b)) : 100;
    const yRange = yDataMax - yDataMin || 1;
    const yPadding = yRange * 0.1;
    const yMin = Math.max(0, yDataMin - yPadding);
    const yMax = yDataMax + yPadding;

    // 실제 중앙값 (50퍼센타일)
    const medianX = percentile(finiteXValues, 0.5);
    const medianY = percentile(yValues, 0.5);

    return {
      xMin,
      xMax,
      yMin,
      yMax,
      newItemX,
      newItemThreshold,
      medianX,
      medianY,
    };
  }, [slicedData]);

  const points = useMemo(() => {
    return slicedData.map((issue) => {
      const rawChange = getChangePercentage(
        issue.comparisonPeriodCount,
        issue.selectedPeriodCount,
      );
      // null (신규) 또는 비정상 값은 "New" 영역에 배치, 유한 값은 그대로 사용
      const x =
        rawChange === null || !Number.isFinite(rawChange)
          ? axisConfig.newItemX
          : rawChange;
      return {
        x,
        y: issue.selectedPeriodCount,
        issue,
        rawChange,
      };
    });
  }, [slicedData, axisConfig.newItemX]);

  const visiblePoints = useMemo(
    () => points.filter((p) => !hiddenSeriesIds.has(p.issue.name)),
    [points, hiddenSeriesIds],
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'Issues',
          data: visiblePoints,
          backgroundColor: visiblePoints.map((point) =>
            getChartColorByHash(point.issue.name),
          ),
          borderColor: visiblePoints.map((point) =>
            getChartColorByHash(point.issue.name),
          ),
          pointRadius: 7,
        },
      ],
    }),
    [visiblePoints],
  );

  const handleToggleSeries = useCallback(
    (issueName: string) => {
      setHiddenSeriesIds((prev) => {
        const visibleCount = slicedData.filter((issue) => !prev.has(issue.name)).length;
        const isCurrentlyVisible = !prev.has(issueName);
        if (isCurrentlyVisible && visibleCount === 1) return prev;
        const next = new Set(prev);
        if (next.has(issueName)) {
          next.delete(issueName);
        } else {
          next.add(issueName);
        }
        return next;
      });
    },
    [slicedData],
  );

  const handleIsolateSeries = useCallback(
    (issueName: string) => {
      setIsolatedSeriesId((currentIsolated) => {
        if (currentIsolated === null) {
          setPreIsolateHiddenIds(new Set(hiddenSeriesIds));
          const allNames = new Set(slicedData.map((issue) => issue.name));
          const hiddenSet = new Set(allNames);
          hiddenSet.delete(issueName);
          setHiddenSeriesIds(hiddenSet);
          return issueName;
        } else if (currentIsolated === issueName) {
          setHiddenSeriesIds(preIsolateHiddenIds ?? new Set());
          setPreIsolateHiddenIds(null);
          return null;
        } else {
          const allNames = new Set(slicedData.map((issue) => issue.name));
          const hiddenSet = new Set(allNames);
          hiddenSet.delete(issueName);
          setHiddenSeriesIds(hiddenSet);
          return issueName;
        }
      });
    },
    [slicedData, hiddenSeriesIds, preIsolateHiddenIds],
  );

  const handleShowAll = useCallback(() => {
    setHiddenSeriesIds(new Set());
    setIsolatedSeriesId(null);
    setPreIsolateHiddenIds(null);
  }, []);

  const handlePointClick = useCallback(
    (event: any, elements: any[]) => {
      if (elements.length === 0) {
        dropdownState.close();
        return;
      }

      const element = elements[0];
      const chart: ChartJS<'scatter'> = event.chart;
      if (!chart) return;

      const datasetIndex = element.datasetIndex;
      const index = element.index;
      const point = chart.data.datasets[datasetIndex].data[index] as {
        x: number;
        y: number;
        issue: CompositedSignalIssue;
      };

      if (!point || !point.issue) return;

      // 클릭된 포인트의 픽셀 위치 계산
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      const xPixel = xScale.getPixelForValue(point.x);
      const yPixel = yScale.getPixelForValue(point.y);

      // 차트 컨테이너의 위치 가져오기
      const chartContainer = chart.canvas.getBoundingClientRect();

      // 드롭다운 위치 설정 (클릭 위치 기준)
      dropdownState.open({
        modalState: point.issue,
        pos: {
          top: chartContainer.top + yPixel,
          left: chartContainer.left + xPixel,
        },
      });
    },
    [dropdownState],
  );

  const options: ChartOptions<'scatter'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: handlePointClick,
      scales: {
        x: {
          title: {
            display: true,
            text: `${getPeriodLabel(props.datePreset)} change`,
          },
          min: axisConfig.xMin,
          max: axisConfig.xMax,
          ticks: {
            callback: (value) => {
              const v = typeof value === 'string' ? Number(value) : value;
              if (v >= axisConfig.newItemThreshold) return 'New';
              return `${Math.round(v)}%`;
            },
          },
        },
        y: {
          min: axisConfig.yMin,
          max: axisConfig.yMax,
          ticks: {
            callback: (value) => {
              const v = typeof value === 'string' ? Number(value) : value;
              return Math.round(v);
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const point = context.raw as {
                issue: CompositedSignalIssue;
                rawChange: number | null;
              };

              const displayChanges =
                point.rawChange === null
                  ? 'New'
                  : `${commaNumber(point.rawChange.toFixed(1))}%`;

              return [
                point.issue.name,
                `Signals: ${point.issue.signalTitles.length > 0 ? point.issue.signalTitles.join(', ') : '-'}`,
                `${replaceFeedbackTerminology('Feedback count')}: ${commaNumber(point.issue.selectedPeriodCount)}`,
                `${getPeriodLabel(props.datePreset)} change: ${displayChanges}`,
              ];
            },
          },
        },
        datalabels: {
          display: true,
          formatter: (_, context) => {
            const point = context.dataset.data[context.dataIndex] as {
              x: number;
              y: number;
              issue: CompositedSignalIssue;
            };
            return point.issue.name;
          },
          anchor: 'center',
          align: (context) => {
            const point = context.dataset.data[context.dataIndex] as {
              x: number;
              y: number;
            };
            // 위쪽 사분면이면 'bottom', 아래쪽 사분면이면 'top'
            return point.x > axisConfig.medianX ? 'left' : 'right';
          },
          offset: 6,
          color: visiblePoints.map(() => colors.dune600),
          font: {
            family: 'Instrument Sans',
            size: 13,
            weight: 600,
          },
          textStrokeWidth: 1.5,
          textStrokeColor: colors.dune0,
        },
        quadrantBackground: {
          medianX: axisConfig.medianX,
          medianY: axisConfig.medianY,
          colors: {
            topRight: MATRIX_INFO_CONFIG.get('Dominant').color,
            topLeft: MATRIX_INFO_CONFIG.get('Established').color,
            bottomRight: MATRIX_INFO_CONFIG.get('Rising').color,
            bottomLeft: MATRIX_INFO_CONFIG.get('Niche').color,
            border: colors.dune500,
          },
        },
      },
    }),
    [
      handlePointClick,
      axisConfig,
      props.datePreset,
      visiblePoints,
      replaceFeedbackTerminology,
    ],
  );

  return (
    <div className="flex flex-col gap-6 h-fit p-10">
      <MatrixInfo />
      <div className="relative h-160 w-full" style={{ zIndex: 1 }}>
        <Scatter
          ref={chartRef}
          options={options}
          data={chartData}
          plugins={[quadrantBackgroundPlugin, ChartDataLabels]}
        />
        <IssueActionDropdown
          selectedIssue={dropdownState.modalState}
          dynamicFilterSet={dynamicFilterSet}
          datePreset={datePreset}
          dropdownPosition={dropdownState.pos}
          isDropdownOpen={dropdownState.status}
          closeDropdown={dropdownState.close}
        />
      </div>
      <IssueLegend
        data={slicedData}
        dynamicFilterSet={dynamicFilterSet}
        datePreset={datePreset}
        onToggle={handleToggleSeries}
        onIsolate={handleIsolateSeries}
        onShowAll={handleShowAll}
        hiddenIssueNames={hiddenSeriesIds}
      />
    </div>
  );
};

const MATRIX_LEGEND_TOOLTIPS: Record<string, string> = {
  Dominant:
    'Above-average volume and change rate compared to other issues. Consider prioritizing these.',
  Established:
    'Above-average volume but below-average change compared to other issues. Familiar issues that remain consistent.',
  Rising:
    'Below-average volume but above-average change compared to other issues. Potentially growing — worth monitoring.',
  Niche:
    'Below-average in both volume and change compared to other issues. May require less immediate attention.',
};

export const MatrixInfo = () => {
  return (
    <div className="flex items-center justify-center gap-6">
      {Array.from(MATRIX_INFO_CONFIG.entries()).map(
        ([key, { label, legendColor }]) => (
          <Tooltip
            key={key}
            content={
              <p className="max-w-[300px]">{MATRIX_LEGEND_TOOLTIPS[key]}</p>
            }
          >
            <div className="flex items-center gap-1.5 text-[13px] text-foreground cursor-default">
              <span
                className="inline-block w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: legendColor }}
              />
              {label}
            </div>
          </Tooltip>
        ),
      )}
    </div>
  );
};
